import React, { useContext, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/css';
import { ITaxonomyTerms } from '@kentico/kontent-delivery';
import { Checkbox } from '@chakra-ui/react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { FilterTaxonomy } from 'api/queries/filter';
import { DrainContext, FilterCurrentElement } from 'contexts/DrainContext';
import { EMPTY_ELEMENT } from 'common/utils/react';
import { EMPTY_STRING } from 'common/utils/string';
import { Color } from 'css/theme';

const classes = {
  root: css`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
  `,
  taxonomy: {
    root: css`
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      width: 100%;
    `,
  },
  rootItem: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    height: 57px;
    padding: 13px 20px 10px 20px;
    border: 1px solid ${Color.Gray100};
  `,
  title: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    height: 57px;
    padding: 13px 20px 10px 20px;
    font-weight: 600;
  `,
  search: {
    root: css`
      position: relative;
      padding: 0 20px 0 20px;
    `,
    placeholder: css`
      ::placeholder {
        color: ${Color.Gray800};
      }
    `,
    input: css`
      border: 1px solid ${Color.Purple400};
    `,
    icon: css`
      padding: 20px;
      position: absolute;
      right: 20px;
      top: -9px;
      color: ${Color.Gray800};
    `,
  },
  item: {
    section: css`
      height: 100%;
      overflow-y: auto;
    `,
    root: css`
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      width: 100%;
      height: 57px;
      padding: 13px 20px 10px 20px;
      //border: 1px solid #F0EDED;
    `,
    label: css`
      font-weight: 400;
      padding-top: 2px;
      padding-left: 3px; // @note: 11px is required, Chakra returns 8px margin left
      font-size: 16px;
    `,
    action: css`
      color: ${Color.Purple400};
      cursor: pointer;
      padding-top: 3px;
    `,
    checkbox: css``,
  },
  noResult: css`
    font-weight: bold;
    font-size: 16px;
    padding: 13px 20px 10px 20px;
    width: 100%;
    text-align: center;
  `,
};

function getSearchPlaceholder(filterList: FilterTaxonomy, code: string): string {
  return (
    {
      interest: filterList.filter.options.interest.search.title || `What's their interests?`,
      audience: filterList.filter.options.audience.search.title || 'Who is it for?',
      occasion: filterList.filter.options.occasion.search.title || `What's the occasion?`,
      location: filterList.filter.options.location.search.title || 'Search State/Province',
    }[code] || 'Search...'
  );
}

const SEARCH_FILTER_ITEM_COUNT = 8;

type Props = {
  code: string;
  list: FilterTaxonomy;
};

export const FilterItem: React.FC<Props> = ({ code, list }) => {
  const drain = useContext(DrainContext);

  const [search, setSearch] = useState<string>(EMPTY_STRING);
  const [filterList, setFilterList] = useState<ITaxonomyTerms[]>(list.elements[code].terms);

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFilterList(
      list.elements[code].terms.filter(({ name }: ITaxonomyTerms) => {
        const value = search.toLocaleLowerCase();
        return name.search(new RegExp(value, 'gi')) !== -1;
      })
    );
  }, [search]);

  return (
    <div className={classes.root} ref={rootRef}>
      <div className={classes.title}>{list.elements[code].name}</div>
      {list.elements[code].terms.length >= SEARCH_FILTER_ITEM_COUNT ? (
        <div className={classes.search.root}>
          <input
            name="search"
            value={search}
            placeholder={getSearchPlaceholder(list, code)}
            className={classNames([
              'text-base bg-white h-12 pr-12 pl-6 lg:pl-6 sm:pl-6 outline-none focus:outline-none focus:shadow-sm w-full rounded-full',
              classes.search.placeholder,
              classes.search.input,
            ])}
            onChange={event => {
              const value: string = event.target.value;
              setSearch(value);
            }}
          />
          <div className={classes.search.icon}>
            {search.length > 0 ? (
              <FontAwesomeIcon
                icon={['far', 'times']}
                className={classNames({
                  'cursor-pointer': search.length > 0,
                })}
                onClick={() => {
                  if (search.length > 0) {
                    setSearch(EMPTY_STRING);
                  }
                }}
              />
            ) : (
              <FontAwesomeIcon icon={['far', 'search']} />
            )}
          </div>
        </div>
      ) : (
        EMPTY_ELEMENT
      )}
      <div className={classes.item.section}>
        {filterList.map(({ name, codename }: ITaxonomyTerms, index: number) => {
          return (
            <div className={classes.item.root} key={index}>
              <div className={classes.item.checkbox}>
                <Checkbox
                  size={'lg'}
                  key={codename}
                  name={name}
                  isChecked={drain.filter.elements
                    .map(({ codename }: FilterCurrentElement) => codename)
                    // @todo: the problem with the equal codeNames,
                    // codeName mapper is required as (codename: string) => `${parent.codename}_${codename}
                    .includes(codename)}
                  onChange={() => {
                    drain.appendFilter({
                      parent: {
                        parent: void 0,
                        name: list.elements[code].name,
                        codename: code,
                      },
                      name,
                      codename,
                    });
                  }}
                >
                  <div className={classes.item.label}>{name}</div>
                </Checkbox>
              </div>
            </div>
          );
        })}
      </div>
      {filterList.length === 0 ? <div className={classes.noResult}>No search result</div> : EMPTY_ELEMENT}
    </div>
  );
};
